import React, { memo, useEffect, useRef, useState } from 'react'

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { Box } from '@mui/material'
import { useTheme } from '@mui/material'

import testHandle from '../../utils/testHandle'
import { CenteredLoadingSpinner } from '../LoadingSpinner/LoadingSpinner'
import ZoomControls from './ZoomControls'
import { Div, FullWidthPdfDocument, StyledPage, ZoomControlsWrapper } from './styles'

const stepSize = 0.2
const minScale = 0.6
const maxScale = 1.4

const useReactPdf = () => {
  const [loading, setLoading] = useState(true)
  const [module, setModule] = useState<typeof import('react-pdf')>()

  const hasStartedLoading = useRef(false)

  useEffect(() => {
    if (!hasStartedLoading.current) {
      hasStartedLoading.current = true
      import('react-pdf').then((reactPdf) => {
        // Must set workerSrc AFTER importing react-pdf, because react-pdf's
        // module code sets GlobalWorkerOptions.workerSrc = "pdf.worker.mjs" which overrides any prior setting
        const workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).href
        reactPdf.pdfjs.GlobalWorkerOptions.workerSrc = workerSrc
        reactPdf.pdfjs.GlobalWorkerOptions.workerPort = null
        setModule(reactPdf)
        setLoading(false)
      })
    }
  }, [module])

  return {
    loading,
    module,
  }
}

type Props = {
  base64: string
  id?: string
  onLoad?: () => void
  showControls?: boolean
  showBorder?: boolean
}

type PdfType = {
  numPages: number
}

const options = {
  cMapUrl: '/cmaps/',
  standardFontDataUrl: '/standard_fonts/',
}

const PdfRenderer = ({ id = '', base64, onLoad, showControls = true, showBorder = true }: Props) => {
  const theme = useTheme()
  const [numPages, setNumPages] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [scale, setScale] = useState(maxScale)

  const handleLoadSuccess = ({ numPages }: PdfType) => {
    setLoaded(true)
    setNumPages(numPages)
    onLoad && onLoad()
  }

  const handleUpdateScale = (stepSize: number) => {
    const newScale = Math.min(maxScale, Math.max(minScale, scale + stepSize))
    setScale(newScale)
  }

  const { module: reactPdf, loading } = useReactPdf()

  if (loading) {
    return <CenteredLoadingSpinner />
  }

  /**
   * Below we render the styled components "as" the
   * actual components, since we lazy load react-pdf
   */
  return (
    <Div id={id} data-testid={testHandle(id)}>
      {reactPdf?.Document && (
        <FullWidthPdfDocument
          as={reactPdf.Document}
          file={`data:application/pdf;base64,${base64}`}
          onLoadSuccess={handleLoadSuccess}
          loading={
            <Box p={1}>
              <CenteredLoadingSpinner />
            </Box>
          }
          fullWidth={!loaded}
          options={options}
        >
          {Array.from(new Array(numPages), (el, index) => {
            const nextIndex = index + 1
            const finalIndex = index === numPages - 1
            return (
              <Box key={index} mb={finalIndex ? 0 : theme.spacers.size32}>
                <StyledPage
                  key={`page_${nextIndex}`}
                  pageNumber={nextIndex}
                  scale={scale}
                  loading={
                    <Box p={1}>
                      <CenteredLoadingSpinner />
                    </Box>
                  }
                  border={showBorder}
                  as={reactPdf!.Page}
                />
              </Box>
            )
          })}
        </FullWidthPdfDocument>
      )}
      {showControls && (
        <ZoomControlsWrapper>
          <ZoomControls stepSize={stepSize} updateScale={handleUpdateScale} />
        </ZoomControlsWrapper>
      )}
    </Div>
  )
}

export default memo(PdfRenderer)
